<template>
  <div class="customer-statistic-details">
    <v-card class="mb-5 rounded-lg elevation-2">
      <v-card-title class="d-flex align-center py-4">
        <v-btn icon class="mr-2" title="Quay lại báo cáo" @click="$router.push('/customer-report')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="text-h5 font-weight-medium">{{ statistic.title }}</div>
          <div class="text-body-2 grey--text mt-1">{{ statistic.description }}</div>
        </div>
      </v-card-title>
    </v-card>

    <v-card class="rounded-lg elevation-2">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              outlined
              dense
              clearable
              hide-details
              label="Tìm theo tên, số điện thoại hoặc email"
              prepend-inner-icon="mdi-magnify"
              @input="onSearchInput"
              @keyup.enter="fetchCustomers(1, perPage)"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filterStartDate"
              type="date"
              label="Từ ngày"
              prepend-inner-icon="mdi-calendar-start"
              outlined
              dense
              clearable
              hide-details
              :max="filterEndDate || undefined"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filterEndDate"
              type="date"
              label="Đến ngày"
              prepend-inner-icon="mdi-calendar-end"
              outlined
              dense
              clearable
              hide-details
              :min="filterStartDate || undefined"
            />
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center justify-md-end">
            <v-btn color="primary" class="mr-2" @click="applyDateFilter">
              <v-icon left>mdi-filter</v-icon>
              Lọc
            </v-btn>
            <v-btn outlined color="primary" @click="resetFilters">
              <v-icon left>mdi-refresh</v-icon>
              Đặt lại
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-data-table
        :headers="headers"
        :items="customers"
        :loading="loading"
        :server-items-length="totalItems"
        :items-per-page="perPage"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
          'items-per-page-text': 'Số khách hàng mỗi trang',
          'show-first-last-page': true
        }"
        class="elevation-0"
        @update:options="updateOptions"
      >
        <template v-slot:item.index="{ index }">
          {{ ((page - 1) * perPage) + index + 1 }}
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar size="36" color="teal lighten-5" class="mr-3">
              <span class="teal--text text--darken-2 font-weight-bold">{{ getInitials(item.name) }}</span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.name }}</div>
              <div v-if="item.email" class="text-caption grey--text">{{ item.email }}</div>
            </div>
          </div>
        </template>

        <template v-slot:item.order_count="{ item }">
          {{ formatNumber(item.order_count) }}
        </template>

        <template v-slot:item.revenue="{ item }">
          {{ formatCurrency(item.revenue) }}
        </template>

        <template v-slot:item.last_order_date="{ item }">
          {{ formatDate(item.last_order_date) }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn icon color="primary" title="Xem lịch sử mua hàng" @click="viewOrders(item)">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
          <v-btn icon color="teal" title="Mở khách hàng" @click="openCustomer(item)">
            <v-icon>mdi-account-arrow-right</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center grey--text">
            <v-icon size="48" color="grey lighten-1">mdi-account-search-outline</v-icon>
            <div class="mt-3">Không tìm thấy khách hàng phù hợp</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="orderDialog" max-width="1000">
      <v-card class="rounded-lg">
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" class="mr-3">mdi-history</v-icon>
          <div v-if="selectedCustomer">
            <div>Lịch sử mua hàng - {{ selectedCustomer.name }}</div>
            <div class="text-caption grey--text">{{ selectedCustomer.phone || 'Không có số điện thoại' }}</div>
          </div>
          <v-spacer />
          <v-btn icon @click="orderDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0">
          <v-row v-if="selectedCustomer" class="mx-0 px-4 py-3 teal lighten-5">
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text">Số đơn hàng</div>
              <div class="text-h6 font-weight-bold">{{ formatNumber(selectedCustomer.order_count) }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text">Tổng doanh số</div>
              <div class="text-h6 font-weight-bold">{{ formatCurrency(selectedCustomer.revenue) }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption grey--text">Thời gian</div>
              <div class="font-weight-medium mt-1">{{ periodLabel }}</div>
            </v-col>
          </v-row>

          <v-data-table
            :headers="orderHeaders"
            :items="customerOrders"
            :loading="ordersLoading"
            :items-per-page="5"
            :footer-props="{
              'items-per-page-options': [5, 10, 20],
              'items-per-page-text': 'Số đơn mỗi trang'
            }"
            sort-by="create_date"
            sort-desc
          >
            <template v-slot:item.create_date="{ item }">
              {{ formatDate(item.create_date || item.created_at) }}
            </template>
            <template v-slot:item.retail_cost="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.retail_cost) }}</span>
            </template>
            <template v-slot:item.payment="{ item }">
              <v-chip x-small :color="item.debt > 0 ? 'warning' : 'success'" text-color="white">
                {{ item.debt > 0 ? 'Còn nợ' : 'Đã thanh toán' }}
              </v-chip>
            </template>
            <template v-slot:item.products="{ item }">
              <div v-if="item.order_detail && item.order_detail.length">
                <span
                  v-for="(detail, index) in item.order_detail.slice(0, 2)"
                  :key="detail.id || index"
                >
                  {{ detail.product?.name || 'Sản phẩm' }} ({{ detail.quantity }})<span v-if="index < Math.min(item.order_detail.length, 2) - 1">, </span>
                </span>
                <span v-if="item.order_detail.length > 2" class="grey--text">
                  và {{ item.order_detail.length - 2 }} sản phẩm khác
                </span>
              </div>
              <span v-else class="grey--text">Không có dữ liệu</span>
            </template>
            <template v-slot:no-data>
              <div class="pa-6 text-center grey--text">Không có đơn hàng phù hợp</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import reportModule from '@/api/report'
import orderModule from '@/api/order'

const reportActions = reportModule.actions
const orderActions = orderModule.actions

export default {
  name: 'CustomerStatisticDetails',
  data() {
    return {
      loading: false,
      search: this.$route.query.search || '',
      filterStartDate: this.$route.query.start_date || '',
      filterEndDate: this.$route.query.end_date || '',
      searchTimeout: null,
      customers: [],
      page: 1,
      perPage: 10,
      totalItems: 0,
      orderDialog: false,
      ordersLoading: false,
      selectedCustomer: null,
      customerOrders: [],
      orderHeaders: [
        { text: 'Mã đơn', value: 'code', width: '130px' },
        { text: 'Ngày mua', value: 'create_date', width: '130px' },
        { text: 'Tổng tiền', value: 'retail_cost', width: '140px' },
        { text: 'Thanh toán', value: 'payment', width: '130px', sortable: false },
        { text: 'Sản phẩm', value: 'products', sortable: false }
      ],
      statisticTypes: {
        total: { title: 'Tổng số khách hàng', description: 'Toàn bộ khách hàng đang hoạt động' },
        new: { title: 'Khách hàng mới', description: 'Khách hàng có lần mua đầu tiên trong kỳ thống kê' },
        returning: { title: 'Khách hàng mua lại', description: 'Khách hàng đã phát sinh từ hai lần mua trở lên' },
        rate: { title: 'Khách hàng mua lại', description: 'Danh sách dùng để tính tỷ lệ khách hàng mua lại' },
        stopped: { title: 'Khách hàng ngừng mua', description: 'Khách hàng không còn phát sinh mua hàng trong kỳ' },
        vip: { title: 'Khách hàng VIP', description: 'Khách hàng có từ 5 đơn hoặc doanh số từ 10 triệu đồng' },
        top: { title: 'Top khách hàng theo doanh số', description: 'Khách hàng có doanh số cao nhất trong kỳ' },
        complaints: { title: 'Khách hàng có khiếu nại', description: 'Khách hàng có ghi nhận chăm sóc thuộc loại khiếu nại' }
      }
    }
  },
  computed: {
    type() {
      return this.$route.query.type || 'total'
    },
    statistic() {
      return this.statisticTypes[this.type] || this.statisticTypes.total
    },
    startDate() {
      return this.filterStartDate || null
    },
    endDate() {
      return this.filterEndDate || null
    },
    periodLabel() {
      if (!this.startDate || !this.endDate) return 'Tất cả thời gian'
      return `${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)}`
    },
    headers() {
      const columns = [
        { text: 'STT', value: 'index', sortable: false, width: '70px' },
        { text: 'Khách hàng', value: 'name', sortable: false, width: '240px' },
        { text: 'Số điện thoại', value: 'phone', sortable: false, width: '150px' },
        { text: 'Địa chỉ', value: 'address', sortable: false },
        { text: 'Số đơn', value: 'order_count', sortable: false, align: 'right' },
        { text: 'Doanh số', value: 'revenue', sortable: false, align: 'right' },
        { text: 'Mua gần nhất', value: 'last_order_date', sortable: false, width: '150px' }
      ]
      if (this.type === 'complaints') {
        columns.splice(4, 0, { text: 'Số khiếu nại', value: 'complaint_count', sortable: false, align: 'right' })
      }
      columns.push({ text: 'Thao tác', value: 'action', sortable: false, width: '110px' })
      return columns
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
  },
  methods: {
    updateOptions({ page, itemsPerPage }) {
      this.fetchCustomers(page, itemsPerPage)
    },
    fetchCustomers(page = 1, perPage = 10) {
      this.loading = true
      this.page = page
      this.perPage = perPage

      return reportActions.getCustomerStatisticDetails({
        type: this.type,
        start_date: this.startDate,
        end_date: this.endDate,
        search: this.search,
        page,
        per_page: perPage
      }).then(response => {
        this.customers = response.data.data || []
        this.totalItems = response.data.meta?.total || 0
      }).catch(error => {
        console.error('Error loading customer statistic details:', error)
        this.$snackbar.error('Không thể tải danh sách khách hàng')
      }).finally(() => {
        this.loading = false
      })
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.fetchCustomers(1, this.perPage), 350)
    },
    applyDateFilter() {
      if ((this.filterStartDate && !this.filterEndDate) || (!this.filterStartDate && this.filterEndDate)) {
        this.$snackbar.warning('Vui lòng chọn đầy đủ Từ ngày và Đến ngày')
        return
      }
      if (this.filterStartDate && this.filterEndDate && this.filterStartDate > this.filterEndDate) {
        this.$snackbar.warning('Từ ngày không được lớn hơn Đến ngày')
        return
      }

      this.syncFilterQuery()
      this.fetchCustomers(1, this.perPage)
    },
    resetFilters() {
      this.search = ''
      this.filterStartDate = ''
      this.filterEndDate = ''
      this.syncFilterQuery()
      this.fetchCustomers(1, this.perPage)
    },
    syncFilterQuery() {
      this.$router.replace({
        path: this.$route.path,
        query: {
          type: this.type,
          ...(this.filterStartDate ? { start_date: this.filterStartDate } : {}),
          ...(this.filterEndDate ? { end_date: this.filterEndDate } : {}),
          ...(this.search ? { search: this.search.trim() } : {})
        }
      }).catch(() => {})
    },
    openCustomer(item) {
      this.$router.push({ path: '/customer', query: { id: item.id } })
    },
    viewOrders(item) {
      this.selectedCustomer = item
      this.customerOrders = []
      this.orderDialog = true
      this.ordersLoading = true

      orderActions.getListOrders({
        customer_id: item.id,
        type: 1,
        status: 2,
        active: 1,
        start_date: this.startDate,
        end_date: this.endDate,
        per_page: 1000
      }).then(response => {
        this.customerOrders = response.data.data || []
      }).catch(error => {
        console.error('Error loading customer orders:', error)
        this.$snackbar.error('Không thể tải lịch sử mua hàng')
      }).finally(() => {
        this.ordersLoading = false
      })
    },
    getInitials(name) {
      return (name || 'KH').split(' ').map(part => part[0]).join('').substring(0, 2).toUpperCase()
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('vi-VN')
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
    },
    formatDate(value) {
      if (!value) return 'Chưa có'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('vi-VN')
    }
  }
}
</script>

<style scoped>
.customer-statistic-details {
  min-height: 100%;
}
</style>
